import { AutocompleteWithLabel } from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery
} from '@apollo/client'
import { Button, CircularProgress, Paper } from '@material-ui/core'
import { FC, useEffect } from 'react'

import { CHANNELS_QUERY } from '../../graphql/channel'
import { PRICE_CLASSES_LIST_QUERY } from '../../graphql/priceClass'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { getTranslate, lang } from '../../localization'
import { Channel } from '../../types/channel'
import { actionTypes, PriceClass } from '../../types/priceClass'
import {
  RateConnectionList,
  RateConnectionListFilters
} from '../../types/rateConnection'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import useStyles from './styles/PriceClassDistributionsOptionalFilters.styles'

interface InventorySecondaryFiltersBoxProps {
  autoCompleteForRoomType: any
  setAutoCompleteForRoomType: React.Dispatch<any>
  autoCompleteForRatePlan: any
  setAutoCompleteForRatePlan: React.Dispatch<any>
  autoCompleteForChannel: any
  setAutoCompleteForChannel: React.Dispatch<any>
  autoCompleteForPriceClass: any
  setAutoCompleteForPriceClass: React.Dispatch<any>
  propertyCode: string
  inputFilters: RateConnectionListFilters
  setInputFilters: React.Dispatch<
    React.SetStateAction<RateConnectionListFilters>
  >
  getRateConnectionList: LazyQueryExecFunction<
    {
      rateConnectionList: RateConnectionList
    },
    OperationVariables
  >
  loadingForRateConnectionList: boolean
  isRoleProvider: boolean
}

const InventorySecondaryFiltersBox: FC<InventorySecondaryFiltersBoxProps> = ({
  autoCompleteForRoomType,
  setAutoCompleteForRoomType,
  autoCompleteForRatePlan,
  setAutoCompleteForRatePlan,
  autoCompleteForChannel,
  setAutoCompleteForChannel,
  autoCompleteForPriceClass,
  setAutoCompleteForPriceClass,
  propertyCode,
  inputFilters,
  setInputFilters,
  getRateConnectionList,
  loadingForRateConnectionList,
  isRoleProvider
}) => {
  const classes = useStyles()

  const maxVisibleActions = 2
  const LTR = '\u200E' // Left-To-Right Mark

  const [getRoomTypes, { data: roomtypes, loading: loadingForRoomtypes }] =
    useLazyQuery<{
      roomTypes: RoomTypeBrief[]
    }>(ROOMTYPES_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    if (propertyCode) {
      getRoomTypes({
        variables: {
          propertyCode: propertyCode,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: false
        }
      })
    }
  }, [propertyCode])

  const [getRateplans, { data: rateplans, loading: loadingForRatePlans }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    if (autoCompleteForRoomType && autoCompleteForRoomType.value) {
      getRateplans({
        variables: {
          propertyCode: propertyCode,
          roomTypeCode: autoCompleteForRoomType.code,
          showDeletedRatePlans: false,
          showDeactivedRatePlans: false
        }
      })
    }
  }, [autoCompleteForRoomType && autoCompleteForRoomType.value])

  const { data: channelData, loading: loadingForChannel } = useQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const { data: priceClassList, loading: loadingForGetPriceClassesList } =
    useQuery<{
      priceClasses: PriceClass[]
    }>(PRICE_CLASSES_LIST_QUERY, {
      notifyOnNetworkStatusChange: true,
      skip: isRoleProvider
    })

  useEffect(() => {
    setInputFilters({
      ...inputFilters,
      roomTypeCode: autoCompleteForRoomType && autoCompleteForRoomType.code,
      ratePlanCode: autoCompleteForRatePlan && autoCompleteForRatePlan.code,
      channelCode: autoCompleteForChannel && autoCompleteForChannel.code,
      priceClassID: autoCompleteForPriceClass && autoCompleteForPriceClass.value
    })
  }, [
    autoCompleteForRoomType,
    autoCompleteForRatePlan,
    autoCompleteForChannel,
    autoCompleteForPriceClass
  ])

  const onResetClick = () => {
    setAutoCompleteForRoomType(null)
    setAutoCompleteForRatePlan(null)
    setAutoCompleteForChannel(null)
    setAutoCompleteForPriceClass(null)
  }

  const onApplyClick = () => {
    getRateConnectionList({
      variables: {
        input: inputFilters
      }
    })
  }

  const options = priceClassList?.priceClasses
    ? priceClassList.priceClasses
        .filter((ele) => !ele.archivedAt)
        .map((pr) => {
          const name = lang === 'fa' ? pr.nameTranslatedList[0]?.value : pr.name

          const actions = pr.priceActions
            .slice(0, maxVisibleActions)
            .map((i, index) => {
              const actionType = actionTypes.find(
                (item) => item.value === i.actionType
              )?.label

              const amountText = i.actionAmount.amount
                ? `${LTR}${i.actionAmount.amount}${
                    i.actionAmount.type === 'FixedFee'
                      ? ` ${getTranslate(i.actionAmount.toCurrency || '')}`
                      : '%'
                  }`
                : ''

              const separator =
                index < maxVisibleActions - 1 &&
                index < pr.priceActions.length - 1
                  ? lang === 'fa'
                    ? ' و '
                    : ' + '
                  : ''

              const entityName = i.beneficiary?.entityName
                ? `${getTranslate(' for ')}${i.beneficiary?.entityName}`
                : ''

              return `${amountText} ${getTranslate(
                actionType || ''
              )} ${entityName}${separator}`
            })
            .join('')

          const ellipsis =
            pr.priceActions.length > maxVisibleActions
              ? lang === 'fa'
                ? ' و ...'
                : ' + ...'
              : ''

          return {
            label: `${name} "${actions}${ellipsis}"`,
            value: pr.id || ''
          }
        })
    : []

  return (
    <div className={classes.root}>
      <Paper elevation={1} className={classes.paper}>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            value={autoCompleteForRoomType}
            label={getTranslate('Room Type')}
            placeholder={getTranslate('Select Room type')}
            includeInputInList
            onChange={(_, v: any) => {
              if (v) {
                setAutoCompleteForRoomType({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
              } else {
                setAutoCompleteForRoomType(null)
                setAutoCompleteForRatePlan(null)
              }
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              roomtypes?.roomTypes?.map((roomtype) => ({
                label: `${roomtype.name} - (${roomtype.displayRoomTypeCode})`,
                value: roomtype.id,
                code: roomtype.displayRoomTypeCode
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            inputSize="small"
            loading={loadingForRoomtypes}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            label={getTranslate('Rateplan')}
            placeholder={getTranslate('Select Rateplan')}
            includeInputInList
            onChange={(_, v: any) => {
              if (v) {
                setAutoCompleteForRatePlan({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
              } else {
                setAutoCompleteForRatePlan(null)
              }
            }}
            value={autoCompleteForRatePlan}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              rateplans?.ratePlans.map((rateplan) => ({
                label: `${rateplan.name} - (${rateplan.displayRatePlanCode})`,
                value: rateplan.id,
                code: rateplan.displayRatePlanCode
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            inputSize="small"
            loading={loadingForRatePlans}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            label={getTranslate('Channel')}
            placeholder={getTranslate('Select Channel')}
            value={autoCompleteForChannel}
            includeInputInList
            onChange={(_, value: any) => {
              if (value) {
                setAutoCompleteForChannel({
                  value: value.value,
                  label: value.label,
                  code: value.code
                })
              } else {
                setAutoCompleteForChannel(null)
              }
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              channelData?.channels
                ? channelData.channels.map((item) => ({
                    label: item.basicDetails.name,
                    value: item.basicDetails.channelID || '',
                    code: item.basicDetails.displayChannelCode
                  }))
                : []
            }
            renderOption={(option) => <>{option.label}</>}
            inputSize="small"
            loading={loadingForChannel}
            loadingText={getTranslate('Please wait...')}
          />
        </div>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            noHelperText
            fullWidth
            value={autoCompleteForPriceClass}
            label={getTranslate('Price Class')}
            placeholder={getTranslate('Select Price Class')}
            options={options}
            onChange={(_, v: any) => {
              if (v) {
                setAutoCompleteForPriceClass({
                  value: v.value,
                  label: v.label
                })
              } else {
                setAutoCompleteForPriceClass(null)
              }
            }}
            disabled={isRoleProvider}
            inputSize="small"
            getOptionLabel={(option) => option.label || ''}
            getOptionSelected={(option, value) => option.value === value.value}
            loading={loadingForGetPriceClassesList}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div />
        <div className={classes.buttonsContainer}>
          <Button className={classes.cancelButton} onClick={onResetClick}>
            {getTranslate('Reset')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={onApplyClick}
            disabled={loadingForRateConnectionList}>
            {getTranslate('Apply')}
            {loadingForRateConnectionList && (
              <CircularProgress
                className={classes.circularProgress}
                size={16}
                color="primary"
              />
            )}
          </Button>
        </div>
      </Paper>
    </div>
  )
}

export default InventorySecondaryFiltersBox
